// RM-CAN-14 (the principal, 20 September 2026: "Why does the rail change at
// all other than what is selected"): THE ONE NAVIGATION MODEL of the Room
// Rail. A World fills it from its room index (`projectRoomRailGroups`); Local
// fills it from its project folders and ungoverned threads. The rail renders
// the model and never asks which stance filled it — the pattern the room
// already uses (`PrimitiveFrameOrgans`).
//
// Local never wears a governed record: no synthetic `world-ready` state, no
// invented R_/S_ id. A Local row's id is `environmentId:threadId` and a
// folder's is `environmentId:projectId`; a World's rows keep the World's ids.

import { projectEntitlementTree, type EntitlementSessionNode } from "./entitlementTree";
import { localSessionLabel, localSessionNodeId, type LocalSessionShellInput } from "./localView";
import type { RoomRailGroups, RoomRailSessionNode } from "./projectRoomRailGroups";

export interface RailThreadAddress {
  readonly environmentId: string;
  readonly threadId: string;
}

export interface RailSessionRow {
  readonly id: string;
  readonly label: string;
  /** The parent row's id; null directly under a group. */
  readonly parentId: string | null;
  readonly groupId: string;
  /** Local: the thread the row IS. World: null — a session binds its thread through its executions. */
  readonly thread: RailThreadAddress | null;
  /** The row stands in the addressed lineage. */
  readonly addressed: boolean;
  readonly children: ReadonlyArray<RailSessionRow>;
}

export interface RailGroupRow {
  readonly id: string;
  readonly label: string;
  /** A World's general or workroom, or a Local project folder. */
  readonly kind: "general" | "workroom" | "folder";
  /** The World's standing general room; never a folder. */
  readonly standing: boolean;
  readonly sessions: ReadonlyArray<RailSessionRow>;
  /**
   * Live members that keep no seat because they are parked (Local: a snoozed
   * or settled thread). Listed rows plus this is the group's live membership.
   */
  readonly parked: number;
}

export interface RailNavigationModel {
  readonly authority: "world" | "local";
  readonly groups: ReadonlyArray<RailGroupRow>;
  /** Every row id on the addressed lineage, the addressed row included. */
  readonly addressed: ReadonlySet<string>;
}

/** One World session node as a rail row; its children ride along. */
export function worldRailRow(node: RoomRailSessionNode, parentId: string | null): RailSessionRow {
  return {
    id: node.session.sessionId,
    label: node.session.label,
    parentId,
    groupId: node.session.roomId,
    thread: null,
    addressed: node.addressed,
    children: node.children.map((child) => worldRailRow(child, node.session.sessionId)),
  };
}

export function worldRailNavigation(groups: RoomRailGroups): RailNavigationModel {
  return {
    authority: "world",
    groups: groups.rooms.map((entry) => ({
      id: entry.room.roomId,
      label: entry.room.label,
      kind: entry.room.kind === "workroom" ? "workroom" : "general",
      standing: entry.standing,
      sessions: entry.sessions.map((node) => worldRailRow(node, null)),
      // A World parks nothing on this rail: retirement has its own group.
      parked: 0,
    })),
    addressed: groups.addressed,
  };
}

export interface LocalRailThreadInput extends LocalSessionShellInput {
  readonly projectId: string;
  /**
   * The thread's EFFECTIVE lifecycle, as `shellToStripThread` resolves it
   * (capabilities, auto-settle, a snooze that outranks a pin): non-null means
   * it holds. Local's strip ordered and parked rows by these; so does the rail.
   */
  readonly pinnedAt: string | null;
  readonly snoozedUntil: string | null;
  readonly settledAt: string | null;
}

export interface LocalRailProjectInput {
  readonly environmentId: string;
  readonly projectId: string;
  readonly title: string;
  readonly createdAt: string;
}

export function localRailFolderId(environmentId: string, projectId: string): string {
  return `${environmentId}:${projectId}`;
}

/**
 * Local's fill. Tree law is the entitlement fold's (archived rows are gone, a
 * live child of a missing parent surfaces as a root, cycles break). EVERY
 * project is a folder, the empty ones included: an empty folder is where a
 * first conversation starts, and leaving it out made a new project invisible
 * (review, 20 September 2026). Folders holding a live Local thread lead, then
 * the empty ones; oldest first within each — the born order a World's rooms keep.
 *
 * Seats and order are the law Local's strip kept (rootSessionStrip.tsx, restored
 * after review, 20 September 2026). A parked thread — snoozed or settled — keeps
 * a seat only while it stands on the addressed lineage or a descendant keeps a
 * seat in its own right. Siblings stand pinned first, then active, then the
 * parked that kept a seat, each in the given order.
 */
export function localRailNavigation(input: {
  readonly projects: ReadonlyArray<LocalRailProjectInput>;
  readonly threads: ReadonlyArray<LocalRailThreadInput>;
  /** The addressed thread's row id (`environmentId:threadId`), or null. */
  readonly activeId: string | null;
}): RailNavigationModel {
  const local = input.threads.filter((thread) => thread.worldKey === null);
  const folderByRow = new Map(
    local.map((thread) => [
      localSessionNodeId(thread.environmentId, thread.threadId),
      localRailFolderId(thread.environmentId, thread.projectId),
    ]),
  );
  const roots =
    projectEntitlementTree({ worldKeys: [], threads: local }).find(
      (group) => group.worldKey === null,
    )?.sessions ?? [];

  const parentById = new Map<string, string | null>();
  const index = (node: EntitlementSessionNode, parentId: string | null): void => {
    const id = localSessionNodeId(node.environmentId, node.threadId);
    parentById.set(id, parentId);
    for (const child of node.children) index(child, id);
  };
  for (const root of roots) index(root, null);
  const addressed = new Set<string>();
  let cursor = input.activeId !== null && parentById.has(input.activeId) ? input.activeId : null;
  while (cursor !== null && !addressed.has(cursor)) {
    addressed.add(cursor);
    cursor = parentById.get(cursor) ?? null;
  }

  const lifecycle = new Map(
    local.map((thread) => [
      localSessionNodeId(thread.environmentId, thread.threadId),
      {
        pinned: thread.pinnedAt !== null,
        parked: thread.snoozedUntil !== null || thread.settledAt !== null,
      },
    ]),
  );
  const idOf = (node: EntitlementSessionNode) =>
    localSessionNodeId(node.environmentId, node.threadId);
  const parked = (node: EntitlementSessionNode) => lifecycle.get(idOf(node))?.parked === true;
  const qualifies = (node: EntitlementSessionNode): boolean =>
    !parked(node) || addressed.has(idOf(node)) || node.children.some(qualifies);
  const seated = (
    nodes: ReadonlyArray<EntitlementSessionNode>,
  ): ReadonlyArray<EntitlementSessionNode> => {
    const kept = nodes.filter(qualifies);
    const pinned = (node: EntitlementSessionNode) => lifecycle.get(idOf(node))?.pinned === true;
    return [
      ...kept.filter((node) => !parked(node) && pinned(node)),
      ...kept.filter((node) => !parked(node) && !pinned(node)),
      ...kept.filter(parked),
    ];
  };
  const countNodes = (nodes: ReadonlyArray<EntitlementSessionNode>): number =>
    nodes.reduce((total, node) => total + 1 + countNodes(node.children), 0);
  const countRows = (rows: ReadonlyArray<RailSessionRow>): number =>
    rows.reduce((total, entry) => total + 1 + countRows(entry.children), 0);

  const row = (
    node: EntitlementSessionNode,
    parentId: string | null,
    groupId: string,
  ): RailSessionRow => {
    const id = idOf(node);
    return {
      id,
      label: localSessionLabel(node.title),
      parentId,
      groupId,
      thread: { environmentId: node.environmentId, threadId: node.threadId },
      addressed: addressed.has(id),
      children: seated(node.children).map((child) => row(child, id, groupId)),
    };
  };
  const rootsByFolder = new Map<string, EntitlementSessionNode[]>();
  for (const root of roots) {
    const groupId = folderByRow.get(idOf(root));
    if (groupId === undefined) continue;
    const nodes = rootsByFolder.get(groupId);
    if (nodes === undefined) rootsByFolder.set(groupId, [root]);
    else nodes.push(root);
  }
  const groups = input.projects
    .map((project) => ({
      project,
      id: localRailFolderId(project.environmentId, project.projectId),
    }))
    .sort(
      (a, b) =>
        Number(rootsByFolder.has(b.id)) - Number(rootsByFolder.has(a.id)) ||
        a.project.createdAt.localeCompare(b.project.createdAt),
    )
    .map((entry): RailGroupRow => {
      const members = rootsByFolder.get(entry.id) ?? [];
      const sessions = seated(members).map((root) => row(root, null, entry.id));
      return {
        id: entry.id,
        label: entry.project.title,
        kind: "folder",
        standing: false,
        sessions,
        parked: countNodes(members) - countRows(sessions),
      };
    });
  return { authority: "local", groups, addressed };
}
